import {
  ContactType,
  PaginationSort,
  PersonPaginationSort,
  UserGroup,
} from '../gql/graphql';
import {
  getFirstPaginatedNode,
  getPaginatedNodes,
  toPagination,
} from './utils';

import { convergeLogger } from '@converge/config';
import { graphql } from '../gql';
import moment from 'moment';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_BROKER_DETAILS = graphql(`
  query getBroker(
    $filters: PersonFilterInput
    $pagination: PaginationRequest
    $sort: PersonPaginationSort
    $licenseSort: PaginationSort
  ) {
    persons(filters: $filters, pagination: $pagination, sort: $sort) {
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }

      edges {
        cursor
        node {
          uuid
          createdAt
          firstName
          primaryContact
          lastName
          agent
          npn
          title
          status
          source
          groups {
            edges {
              node {
                name
                uuid
              }
            }
          }
          uwTerritories {
            edges {
              node {
                uuid
              }
            }
          }
          bdmTerritories {
            edges {
              node {
                uuid
                name
              }
            }
          }

          contacts {
            edges {
              node {
                type
                primary
                value
                uuid
                source
                createdAt
              }
            }
          }
          users {
            edges {
              cursor
              node {
                uuid
                username
                userId
                groups
                type
              }
            }
          }

          teams {
            edges {
              node {
                uuid
                name
                uwTerritories {
                  edges {
                    node {
                      uuid
                      name
                    }
                  }
                }
                users {
                  edges {
                    cursor
                    node {
                      uuid
                      username
                      userId
                      groups
                      type
                    }
                  }
                }
              }
            }
          }
          office {
            uuid
            name
            address {
              uuid
              street1
              street2
              city
              type
              postalCode
              source
              regions {
                edges {
                  cursor
                  node {
                    uuid
                    name
                    iso2Code
                  }
                }
              }
            }
            users {
              edges {
                cursor
                node {
                  uuid
                  username
                  userId
                  groups
                  type
                }
              }
            }
          }

          licenses(pagination: { first: 200, after: 0 }, sort: $licenseSort) {
            edges {
              cursor
              node {
                uuid
                licenseNumber
                perpetual
                expirationDate
                licenseType
                licenseHolder {
                  uuid
                  firstName
                  lastName
                }
                source
                forState
                region {
                  name
                  iso2Code
                  uuid
                }
              }
            }
          }
          entities {
            edges {
              node {
                uuid
                referenceId
                offices {
                  edges {
                    node {
                      name
                      uuid
                    }
                  }
                }
                teams {
                  edges {
                    node {
                      name

                      uuid
                    }
                  }
                }
                users {
                  edges {
                    cursor
                    node {
                      uuid
                      username
                      userId
                      groups
                      type
                    }
                  }
                }
                addresses(filters: { source: PRIMARY }) {
                  edges {
                    cursor
                    node {
                      uuid
                      street1
                      street2
                      city
                      type
                      postalCode
                      source
                      regions {
                        edges {
                          cursor
                          node {
                            uuid
                            name
                            iso2Code
                          }
                        }
                      }
                      countries {
                        edges {
                          cursor
                          node {
                            uuid
                            name
                            iso2Code
                          }
                        }
                      }
                      primary
                    }
                  }
                }
                aliases(
                  filters: { groupSources: true }
                  pagination: { first: 1 }
                ) {
                  edges {
                    cursor
                    queryCursor
                    node {
                      uuid
                      name
                      type
                      sources
                      primary
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);
export function useGetBroker({
  pageNumber = 0,
  pageSize = 15,
  uuid,
  isAgency = true,
  sort,
  enabled,
  licenseSort,
}: {
  pageNumber?: number;
  pageSize?: number;
  uuid?: string;
  isAgency?: boolean;
  enabled?: boolean;
  sort?: PersonPaginationSort;
  licenseSort?: PaginationSort;
}) {
  const { loading, error, data } = useQuery(GET_BROKER_DETAILS, {
    variables: {
      pagination: toPagination(pageNumber, pageSize),
      sort,
      licenseSort,
      filters: {
        agent: isAgency,
        uuid: uuid,
      },
    },
    skip: !enabled,
  });

  const broker = useMemo(() => {
    if (!data || !data.persons || !data.persons.edges) return [];

    return getPaginatedNodes(data.persons).map((edge) => {
      const entity = getPaginatedNodes(edge.entities);

      const alias = getPaginatedNodes(entity[0]?.aliases);

      if (!alias) {
        convergeLogger.error(
          `No primary legal name alias found for broker ${edge.uuid}`
        );
      }

      const alternativeEmails =
        edge?.contacts?.edges
          ?.filter(
            (contact) =>
              contact.node.type === ContactType.Email &&
              contact.node.source !== 'PRIMARY' &&
              contact.node.value &&
              contact.node.value.trim() !== ''
          )
          .sort((a, b) => {
            const dateA = new Date(a.node.createdAt || 0).getTime();
            const dateB = new Date(b.node.createdAt || 0).getTime();
            return dateA - dateB;
          }) || [];

      return {
        id: edge?.uuid ?? '',
        firstName: edge?.firstName ?? '',
        lastName: edge?.lastName ?? '',
        title: edge?.title ?? '',
        email: edge?.contacts?.edges?.filter(
          (contact) =>
            contact.node.type === ContactType.Email &&
            contact.node.source === 'PRIMARY'
        )[0]?.node.value,
        emailUuid: edge?.contacts?.edges?.filter(
          (contact) =>
            contact.node.type === ContactType.Email &&
            contact.node.source === 'PRIMARY'
        )[0]?.node.uuid,
        secondaryEmail: alternativeEmails[0]?.node.value,
        secondaryEmailUuid: alternativeEmails[0]?.node.uuid,
        tertiaryEmail: alternativeEmails[1]?.node.value,
        tertiaryEmailUuid: alternativeEmails[1]?.node.uuid,
        phone: edge?.contacts?.edges?.filter(
          (contact) => contact.node.type === ContactType.Phone
        )[0]?.node.value,
        phoneUuid: edge?.contacts?.edges?.filter(
          (contact) => contact.node.type === ContactType.Phone
        )[0]?.node.uuid,
        officePhone: edge?.contacts?.edges?.filter(
          (contact) => contact.node.type === ContactType.Office
        )[0]?.node.value,
        officePhoneUuid: edge?.contacts?.edges?.filter(
          (contact) => contact.node.type === ContactType.Office
        )[0]?.node.uuid,
        brokerage: entity[0]?.uuid ?? '',
        team: edge?.teams?.edges[0]?.node.uuid,
        primaryAddress: getFirstPaginatedNode(entity[0]?.addresses),
        city: getFirstPaginatedNode(entity[0]?.addresses)?.city,
        region: getFirstPaginatedNode(entity[0]?.addresses)?.regions?.edges[0]
          ?.node?.name,
        onboarded: moment(edge?.createdAt?.split('T')[0]).format('MM/DD/YYYY'),
        licenses: edge?.licenses?.edges,
        officeUuid: edge?.office?.uuid,
        teamUuid: edge?.teams?.edges[0]?.node?.uuid,
        primaryContact: edge?.primaryContact,
        npn: edge?.npn ?? '',
        status: edge?.status ?? '',
        user: edge?.users,
        uWRegion: edge?.uwTerritories?.edges[0]?.node?.uuid,
        bdmRegion: edge?.bdmTerritories?.edges[0]?.node?.uuid,
        teamDBM: edge?.teams?.edges[0]?.node?.users?.edges?.filter(
          (bDMuser) => bDMuser?.node?.type === UserGroup.Bdm
        )[0]?.node?.uuid,
        officeDBM: edge?.office?.users?.edges?.filter(
          (bDMuser) => bDMuser?.node?.type === UserGroup.Bdm
        )[0]?.node?.uuid,
        brokerageDBM: edge?.entities?.edges[0]?.node?.users?.edges?.filter(
          (bDMuser) => bDMuser?.node?.type === UserGroup.Bdm
        )[0]?.node?.uuid,
        teamUW: edge?.teams?.edges[0]?.node?.users?.edges?.filter(
          (uwUser) => uwUser?.node?.type === UserGroup.Underwriter
        )[0]?.node?.uuid,
        officeUW: edge?.office?.users?.edges?.filter(
          (uwUser) => uwUser?.node?.type === UserGroup.Underwriter
        )[0]?.node?.uuid,
        brokerageUW: edge?.entities?.edges[0]?.node?.users?.edges?.filter(
          (uwUser) => uwUser?.node?.type === UserGroup.Underwriter
        )[0]?.node?.uuid,
      };
    });
  }, [data]);

  return {
    error,
    loading,
    data,
    broker,
  };
}
